import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchSubmissions } from '../../api/portal-api.js';
import type { SubmissionListItem, SubmissionListResponse } from '@qdf/shared';

// Grouped status filters — organized by workflow stage
const STATUS_GROUPS: { label: string; filters: { key: string; label: string; color: string; bg: string; match: (i: SubmissionListItem) => boolean }[] }[] = [
  {
    label: 'All',
    filters: [
      { key: '', label: 'All', color: '#1e293b', bg: '#f1f5f9', match: () => true },
    ],
  },
  {
    label: 'Review',
    filters: [
      { key: 'new', label: 'New', color: '#dc2626', bg: '#fee2e2', match: i => i.status === 'new' },
      { key: 'approved', label: 'Approved', color: '#047857', bg: '#d1fae5', match: i => i.status === 'approved' },
      { key: 'rejected', label: 'Rejected', color: '#b91c1c', bg: '#fee2e2', match: i => i.status === 'rejected' },
    ],
  },
  {
    label: 'Funding',
    filters: [
      { key: 'funded', label: 'Funded', color: '#0d9488', bg: '#ccfbf1', match: i => i.status === 'funded' },
    ],
  },
  {
    label: 'Sync',
    filters: [
      { key: 'synced', label: 'Synced', color: '#6d28d9', bg: '#ede9fe', match: i => i.sync_status === 'synced' },
      { key: 'sync_failed', label: 'Sync Failed', color: '#dc2626', bg: '#fee2e2', match: i => i.sync_status === 'failed' },
    ],
  },
];

const ALL_FILTERS = STATUS_GROUPS.flatMap(g => g.filters);

const TYPE_TABS: { key: string; label: string }[] = [
  { key: '', label: 'All Types' },
  { key: 'form', label: 'Onboarding Forms' },
  { key: 'api', label: 'API Disbursements' },
];

// Company filter — legacy records have no lead_source and are Left Main.
const COMPANY_TABS: { key: string; label: string }[] = [
  { key: '', label: 'All Companies' },
  { key: 'leftmain', label: 'Left Main' },
  { key: 'newwestern', label: 'New Western' },
];

const leadSourceOf = (i: SubmissionListItem): string => i.lead_source ?? 'leftmain';

const CLOSE_DATE_TABS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'today', label: 'Today' },
  { key: 'next_7', label: 'Next 7 Days' },
  { key: 'next_30', label: 'Next 30 Days' },
  { key: 'past_due', label: 'Past Due' },
];

export function SubmissionsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || '';

  const [typeFilter, setTypeFilter] = useState<string>('');
  const [companyFilter, setCompanyFilter] = useState<string>(searchParams.get('company') || '');
  const [statusFilter, setStatusFilter] = useState<string>(initialStatus);
  const [closeDateFilter, setCloseDateFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading } = useQuery<SubmissionListResponse>({
    queryKey: ['portal-submissions-all'],
    queryFn: () => fetchSubmissions({ limit: 500 }),
    refetchInterval: 15_000,
  });

  const allItems = data?.items || [];

  // Compute counts per filter key
  const filterCounts = useMemo(() => {
    const counts: Record<string, number> = { '': allItems.length };
    for (const group of STATUS_GROUPS) {
      for (const filter of group.filters) {
        if (filter.key) {
          counts[filter.key] = allItems.filter(filter.match).length;
        }
      }
    }
    return counts;
  }, [allItems]);

  // Get the active filter's match function
  const activeFilter = ALL_FILTERS.find(f => f.key === statusFilter);

  // Filter items
  const filteredItems = useMemo(() => {
    let items = allItems;
    if (companyFilter) items = items.filter(i => leadSourceOf(i) === companyFilter);
    if (typeFilter) items = items.filter(i => i.submission_type === typeFilter);
    if (statusFilter && activeFilter) items = items.filter(activeFilter.match);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(i =>
        i.submission_id.toLowerCase().includes(term) ||
        (i.full_name && i.full_name.toLowerCase().includes(term)) ||
        (i.entity_name && i.entity_name.toLowerCase().includes(term)) ||
        (i.email && i.email.toLowerCase().includes(term)) ||
        (i.partner_name && i.partner_name.toLowerCase().includes(term)) ||
        (i.partner_submission_id && i.partner_submission_id.toLowerCase().includes(term)) ||
        (i.property_address_1 && i.property_address_1.toLowerCase().includes(term))
      );
    }
    if (closeDateFilter) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      items = items.filter(i => {
        if (!i.projected_close_date) return false;
        const d = new Date(i.projected_close_date + 'T00:00:00');
        if (closeDateFilter === 'today') return d.toDateString() === today.toDateString();
        if (closeDateFilter === 'next_7') {
          const end = new Date(today);
          end.setDate(today.getDate() + 7);
          return d >= today && d < end;
        }
        if (closeDateFilter === 'next_30') {
          const end = new Date(today);
          end.setDate(today.getDate() + 30);
          return d >= today && d < end;
        }
        if (closeDateFilter === 'past_due') return d < today;
        return true;
      });
    }
    return items;
  }, [allItems, companyFilter, typeFilter, statusFilter, searchTerm, closeDateFilter, activeFilter]);

  const handleStatusChange = (key: string) => {
    setStatusFilter(key);
    if (key) {
      setSearchParams({ status: key });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.headerRow}>
        <div>
          <h1 style={s.title}>Submissions</h1>
          <p style={s.subtitle}>{allItems.length} total submissions</p>
        </div>
      </div>

      {/* Company Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' as const }}>
          🏢 Company
        </span>
        <div style={{ ...s.typeTabs, marginBottom: 0 }}>
          {COMPANY_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setCompanyFilter(tab.key)}
              style={{ ...s.typeTab, ...(companyFilter === tab.key ? s.typeTabActive : {}) }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Type Tabs */}
      <div style={s.typeTabs}>
        {TYPE_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setTypeFilter(tab.key)}
            style={{ ...s.typeTab, ...(typeFilter === tab.key ? s.typeTabActive : {}) }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Close Date Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' as const }}>
          📅 Close Date
        </span>
        <div style={s.typeTabs}>
          {CLOSE_DATE_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setCloseDateFilter(tab.key)}
              style={{
                ...s.typeTab,
                ...(closeDateFilter === tab.key ? s.typeTabActive : {}),
                ...(tab.key === 'past_due' && closeDateFilter === 'past_due'
                  ? { background: '#fee2e2', color: '#dc2626' }
                  : {}),
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Status Filters — grouped */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' as const, alignItems: 'center' }}>
        {STATUS_GROUPS.map((group, gi) => (
          <div key={group.label} style={{ display: 'contents' }}>
            {gi > 0 && (
              <div style={{ width: '1px', height: '24px', background: '#e2e8f0', margin: '0 4px' }} />
            )}
            {group.filters.map(sc => {
              const count = filterCounts[sc.key] || 0;
              const isActive = statusFilter === sc.key;
              const isUrgent = (sc.key === 'new' || sc.key === 'sync_failed') && count > 0 && !isActive;
              return (
                <button
                  key={sc.key}
                  onClick={() => handleStatusChange(sc.key)}
                  style={{
                    ...s.statusPill,
                    background: isActive ? sc.bg : '#fff',
                    color: isActive ? sc.color : '#475569',
                    border: isActive ? `2px solid ${sc.color}` : '1px solid #e2e8f0',
                    fontWeight: isActive ? 700 : 500,
                    ...(isUrgent ? { boxShadow: `0 0 0 2px ${sc.bg}` } : {}),
                  }}
                >
                  {sc.label}
                  <span style={{
                    ...s.countBadge,
                    background: isActive ? sc.color : (isUrgent ? sc.color : '#e2e8f0'),
                    color: isActive || isUrgent ? '#fff' : '#64748b',
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={s.searchRow}>
        <input
          type="text"
          placeholder="Search by name, email, ID, or address..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={s.searchInput}
        />
        <span style={s.resultCount}>
          Showing {filteredItems.length} of {allItems.length}
        </span>
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={s.loadingRow}>
          <div style={s.spinner} />
          <span style={{ color: '#64748b', fontSize: '14px' }}>Loading submissions...</span>
        </div>
      ) : filteredItems.length === 0 ? (
        <div style={s.emptyState}>
          <span style={{ fontSize: '40px', marginBottom: '12px' }}>📭</span>
          <p style={{ color: '#64748b', fontSize: '15px', margin: 0 }}>No submissions match your filters</p>
          {(statusFilter || typeFilter || companyFilter || searchTerm || closeDateFilter) && (
            <button
              onClick={() => { setStatusFilter(''); setTypeFilter(''); setCompanyFilter(''); setCloseDateFilter(''); setSearchTerm(''); setSearchParams({}); }}
              style={s.clearBtn}
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>ID</th>
                <th style={s.th}>Type</th>
                <th style={s.th}>Name / Property</th>
                <th style={s.th}>Amount</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Proj. Close</th>
                <th style={s.th}>Submitted</th>
                <th style={s.th}>Sync</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => (
                <tr
                  key={item.submission_id}
                  style={s.tr}
                  onClick={() => navigate(`/portal/submissions/${item.submission_id}`)}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <td style={s.td}>
                    <span style={s.idBadge}>{item.submission_id}</span>
                  </td>
                  <td style={s.td}>
                    <span style={{
                      padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                      background: item.submission_type === 'form' ? '#eef2ff' : '#f5f3ff',
                      color: item.submission_type === 'form' ? '#4338ca' : '#7c3aed',
                    }}>
                      {item.submission_type === 'form' ? 'Form' : 'API'}
                    </span>
                    {leadSourceOf(item) === 'newwestern' && (
                      <span style={{
                        marginLeft: '5px', padding: '3px 8px', borderRadius: '4px',
                        fontSize: '11px', fontWeight: 600, background: '#faf6ef', color: '#967448',
                        border: '1px solid #e8dcc8',
                      }}>
                        NW
                      </span>
                    )}
                  </td>
                  <td style={s.td}>
                    <div style={{ fontWeight: 500, color: '#1e293b' }}>
                      {item.full_name || item.property_address_1 || '—'}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '1px' }}>
                      {item.entity_name || (item.property_city && `${item.property_city}, ${item.property_state}`) || ''}
                    </div>
                  </td>
                  <td style={{ ...s.td, fontWeight: 500, color: '#1e293b' }}>
                    {formatCurrency(item.loc_amount_requested || item.fund_amount_requested)}
                  </td>
                  <td style={s.td}>
                    <StatusBadge status={item.status} />
                  </td>
                  <td style={{ ...s.td, color: item.projected_close_date ? (new Date(item.projected_close_date + 'T00:00:00') < new Date(new Date().toDateString()) ? '#dc2626' : '#1e293b') : '#cbd5e1', fontSize: '13px', fontWeight: item.projected_close_date ? 500 : 400 }}>
                    {item.projected_close_date
                      ? new Date(item.projected_close_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '—'}
                  </td>
                  <td style={{ ...s.td, color: '#64748b', fontSize: '13px' }}>
                    {new Date(item.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td style={s.td}>
                    {item.sync_status === 'synced' ? (
                      <span style={{ color: '#059669', fontSize: '13px', fontWeight: 500 }}>✓ Synced</span>
                    ) : item.sync_status === 'failed' ? (
                      <span style={{ color: '#dc2626', fontSize: '13px', fontWeight: 500 }}>✕ Failed</span>
                    ) : item.ready_to_sync ? (
                      <span style={{ color: '#7c3aed', fontSize: '13px', fontWeight: 500 }}>⏳ Queued</span>
                    ) : (
                      <span style={{ color: '#cbd5e1', fontSize: '13px' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatCurrency(amount?: number): string {
  if (!amount) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
}

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'New', color: '#1d4ed8', bg: '#dbeafe' },
  in_review: { label: 'In Review', color: '#b45309', bg: '#fef3c7' },
  approved: { label: 'Approved', color: '#047857', bg: '#d1fae5' },
  rejected: { label: 'Rejected', color: '#b91c1c', bg: '#fee2e2' },
  synced: { label: 'Synced', color: '#6d28d9', bg: '#ede9fe' },
  sync_failed: { label: 'Sync Failed', color: '#dc2626', bg: '#fee2e2' },
  funded: { label: 'Funded', color: '#0d9488', bg: '#ccfbf1' },
};

function StatusBadge({ status }: { status: string }) {
  const info = STATUS_MAP[status] || { label: status, color: '#475569', bg: '#f1f5f9' };
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
      background: info.bg, color: info.color,
    }}>
      {info.label}
    </span>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: { padding: '24px 32px', maxWidth: '1400px', margin: '0 auto', fontFamily: "'Inter', -apple-system, sans-serif" },
  headerRow: { marginBottom: '20px' },
  title: { fontSize: '24px', fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', margin: '3px 0 0' },
  typeTabs: { display: 'flex', gap: '4px', marginBottom: '14px', background: '#f1f5f9', borderRadius: '8px', padding: '2px', width: 'fit-content' },
  typeTab: {
    padding: '6px 16px', borderRadius: '6px', border: 'none',
    background: 'transparent', color: '#64748b', fontSize: '12px', fontWeight: 500,
    cursor: 'pointer', transition: 'all 0.15s',
  },
  typeTabActive: { background: '#fff', color: '#1e293b', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', fontWeight: 600 },
  statusPill: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '5px 12px', borderRadius: '18px',
    fontSize: '12px', cursor: 'pointer', transition: 'all 0.15s',
  },
  countBadge: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: '20px', height: '18px', borderRadius: '9px',
    fontSize: '10px', fontWeight: 700, padding: '0 5px',
  },
  searchRow: { display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' },
  searchInput: {
    flex: 1, padding: '8px 14px', borderRadius: '8px',
    border: '1px solid #e2e8f0', background: '#fff',
    fontSize: '13px', color: '#1e293b', outline: 'none',
    boxSizing: 'border-box' as const,
  },
  resultCount: { fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap' as const },
  tableWrap: {
    background: '#fff', borderRadius: '12px',
    border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: {
    textAlign: 'left' as const, padding: '10px 14px', color: '#94a3b8', fontSize: '10px',
    fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.5px',
    borderBottom: '1px solid #f1f5f9', background: '#fafbfc',
  },
  tr: { cursor: 'pointer', transition: 'background 0.1s', borderBottom: '1px solid #f8fafc' },
  td: { padding: '12px 14px', fontSize: '13px', color: '#334155' },
  idBadge: { fontFamily: 'monospace', fontSize: '12px', color: '#4338ca', fontWeight: 600 },
  loadingRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '80px' },
  spinner: { width: '28px', height: '28px', border: '3px solid #e2e8f0', borderTop: '3px solid #4338ca', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  emptyState: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', padding: '60px 24px' },
  clearBtn: {
    marginTop: '16px', padding: '7px 18px', borderRadius: '8px',
    border: '1px solid #e2e8f0', background: '#fff', color: '#4338ca',
    fontSize: '12px', fontWeight: 500, cursor: 'pointer',
  },
};
